import React, { useState, useEffect } from 'react';
import { BarChart2, RefreshCw } from 'lucide-react';
import ActivityDetails from './ActivityDetails';

const SPORT_TYPES = [
  { id: 'Run', name: 'Carrera' },
  { id: 'Ride', name: 'Ciclismo Exterior' },
  { id: 'VirtualRide', name: 'Ciclo Indoor / Rodillo' },
  { id: 'Swim', name: 'Natación' },
  { id: 'WeightTraining', name: 'Fuerza / Pesas' },
  { id: 'Workout', name: 'Entrenamiento General' },
  { id: 'Walk', name: 'Andar' },
];

const SPORT_EMOJIS = {
  Run: '🏃', Ride: '🚴', VirtualRide: '🚴‍♂️', Walk: '🚶',
  WeightTraining: '💪', Workout: '🧘', Swim: '🏊', Default: '⚡'
};

const WorkoutModal = ({ 
  isOpen, 
  onClose, 
  selectedDate, 
  activitiesDay = [], 
  plannedWorkoutsDay = [], 
  appMode, 
  athleteId,
  onSavePlanned,
  onDeletePlanned,
  onSaveNotes,
  onCompletePlanned
}) => {
  const [selectedIndex, setSelectedIndex] = useState(0);

  // State for Notes (Feedback)
  const [notes, setNotes] = useState('');
  
  // State for Strava Details (Zones and Splits)
  const [stravaDetails, setStravaDetails] = useState(null);
  const [loadingStravaDetails, setLoadingStravaDetails] = useState(false);
  const [showStravaDetails, setShowStravaDetails] = useState(false);
  const [errorStr, setErrorStr] = useState(null);

  // State for Planner
  const [type, setType] = useState('Run');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [duration, setDuration] = useState(60);

  // State for Athlete Check-in
  const [athleteRpe, setAthleteRpe] = useState(5);
  const [athleteNotes, setAthleteNotes] = useState('');

  const [saving, setSaving] = useState(false);

  // Derive the list of tabs/items
  const items = [
    ...activitiesDay.map(a => ({ ...a, itemType: 'completed' })),
    ...plannedWorkoutsDay.map(p => ({ ...p, itemType: 'planned' }))
  ];
  
  // If coach, they can always add a new plan on this day
  if (appMode === 'coach') {
    items.push({ itemType: 'new' });
  } else if (items.length === 0) {
    // If athlete and nothing is here (should theoretically not happen due to calendar logic, but just in case)
    items.push({ itemType: 'empty' });
  }

  // Ensure selectedIndex is within bounds when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedIndex(0);
      setStravaDetails(null);
      setShowStravaDetails(false);
    }
  }, [isOpen, selectedDate]);

  const activeItem = items[selectedIndex] || items[0];
  const isCompleted = activeItem?.itemType === 'completed';
  const isPlanned = activeItem?.itemType === 'planned';
  const isNew = activeItem?.itemType === 'new';
  
  // Constante API URL
  const API_URL = import.meta.env.VITE_API_URL || 
  (window.location.hostname === 'localhost' ? 'http://localhost:3000/api' : 'https://api.plataforma-deportiva.com/api');

  const handleFetchStravaDetails = async () => {
    if (showStravaDetails) {
      setShowStravaDetails(false);
      return;
    }
    setShowStravaDetails(true);
    if (stravaDetails) return; // Ya lo tenemos en caché
    
    setLoadingStravaDetails(true);
    try {
      const url = athleteId 
        ? `${API_URL}/activities/${activeItem.id}/details?userId=${athleteId}`
        : `${API_URL}/activities/${activeItem.id}/details`;

      const res = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (res.ok) {
        const data = await res.json();
        setStravaDetails(data);
        setErrorStr(null);
      } else {
        const text = await res.text();
        setErrorStr(`Backend error (${res.status}): ${text} | Frontend athleteId: ${athleteId}`);
      }
    } catch (err) {
      console.error("Error obteniendo detalles de Strava:", err);
      setErrorStr(err.message);
    } finally {
      setLoadingStravaDetails(false);
    }
  };

  // Reset when changing tabs
  useEffect(() => {
    setStravaDetails(null);
    setShowStravaDetails(false);
  }, [selectedIndex]);
  
  // Sync state when tab changes or data updates
  useEffect(() => {
    // Determine active item inside the effect to avoid object reference issues
    const currentItems = [
      ...activitiesDay.map(a => ({ ...a, itemType: 'completed' })),
      ...plannedWorkoutsDay.map(p => ({ ...p, itemType: 'planned' }))
    ];
    if (appMode === 'coach') {
      currentItems.push({ itemType: 'new' });
    } else if (currentItems.length === 0) {
      currentItems.push({ itemType: 'empty' });
    }
    
    const item = currentItems[selectedIndex] || currentItems[0];
    if (!item) return;

    if (item.itemType === 'completed') {
      setNotes(item.trainerNotes || '');
    } else if (item.itemType === 'planned') {
      setType(item.type || 'Run');
      setTitle(item.title || '');
      setDescription(item.description || '');
      setDuration(item.durationMinutes || 60);
      setAthleteRpe(item.rpe || 5);
      setAthleteNotes(item.athleteNotes || '');
    } else {
      setType('Run');
      setTitle('');
      setDescription('');
      setDuration(60);
      setAthleteRpe(5);
      setAthleteNotes('');
    }
  }, [selectedIndex, activitiesDay, plannedWorkoutsDay, appMode]);

  if (!isOpen) return null;

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      if (isCompleted) {
        await onSaveNotes(activeItem.id, notes);
      } else {
        // Garantizamos que la fecha coincide con el día local seleccionado, 
        // independientemente del huso horario del navegador.
        const y = selectedDate.getFullYear();
        const m = String(selectedDate.getMonth() + 1).padStart(2, '0');
        const d = String(selectedDate.getDate()).padStart(2, '0');
        const safeIsoString = `${y}-${m}-${d}T12:00:00.000Z`;

        if (isPlanned) {
          // It's an edit, pass id
          await onSavePlanned({
            id: activeItem.id, // we might need to support edit on backend, if not it just creates
            date: safeIsoString,
            type,
            title,
            description,
            durationMinutes: duration
          });
        } else {
          // New
          await onSavePlanned({
            date: safeIsoString,
            type,
            title,
            description,
            durationMinutes: duration
          });
        }
      }
      onClose();
    } catch (e) {
      alert('Error: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('¿Seguro que quieres eliminar este entrenamiento planificado?')) return;
    setSaving(true);
    try {
      await onDeletePlanned(activeItem.id);
      onClose();
    } catch (e) {
      alert('Error: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleComplete = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onCompletePlanned(activeItem.id, athleteRpe, athleteNotes);
      onClose();
    } catch (e) {
      alert('Error: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.7)', backdropFilter: 'blur(4px)',
      display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000,
      animation: 'fadeInTab 0.2s ease-out'
    }}>
      <div className="glass-panel" style={{ width: '90%', maxWidth: '550px', padding: '0', position: 'relative', overflow: 'hidden' }}>
        
        <button onClick={onClose} style={{
          position: 'absolute', top: '1rem', right: '1rem', background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '1.5rem', zIndex: 10
        }}>×</button>

        {/* Modal Header with Date */}
        <div style={{ padding: '1.5rem 1.5rem 0.5rem', borderBottom: '1px solid var(--glass-border)', background: 'rgba(255,255,255,0.02)' }}>
          <h2 style={{ marginTop: 0, marginBottom: '0.5rem', color: 'var(--text-primary)', fontSize: '1.3rem' }}>
            {selectedDate?.toLocaleDateString('es-ES', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </h2>
          
          {/* TABS FOR MULTIPLE WORKOUTS */}
          {items.length > 1 && (
            <div style={{ display: 'flex', gap: '0.5rem', overflowX: 'auto', paddingBottom: '0.5rem', marginTop: '1rem' }}>
              {items.map((item, idx) => {
                const isActive = idx === selectedIndex;
                let label = '';
                let color = 'white';
                
                if (item.itemType === 'completed') {
                  label = `${SPORT_EMOJIS[item.type] || '⚡'} Completado`;
                  color = 'var(--health-green)';
                } else if (item.itemType === 'planned') {
                  label = `${SPORT_EMOJIS[item.type] || '📅'} ${item.title || 'Plan'}`;
                  color = 'var(--health-cyan)';
                } else if (item.itemType === 'new') {
                  label = '➕ Añadir Plan';
                  color = 'var(--strava-orange)';
                }

                return (
                  <button 
                    key={idx}
                    onClick={() => setSelectedIndex(idx)}
                    style={{
                      background: isActive ? 'rgba(255,255,255,0.1)' : 'transparent',
                      border: `1px solid ${isActive ? color : 'var(--glass-border)'}`,
                      color: isActive ? color : 'var(--text-muted)',
                      padding: '0.4rem 0.8rem', borderRadius: '20px', fontSize: '0.85rem',
                      cursor: 'pointer', whiteSpace: 'nowrap', fontWeight: isActive ? 'bold' : 'normal',
                      transition: 'all 0.2s'
                    }}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Modal Body */}
        <div style={{ padding: '1.5rem', maxHeight: '70vh', overflowY: 'auto' }}>
          
          {activeItem?.itemType === 'empty' ? (
            <p className="text-muted text-center py-4">No hay entrenamientos planificados para hoy.</p>
          ) : isCompleted ? (
            <>
              <div style={{ padding: '1rem', background: 'rgba(255,255,255,0.05)', borderRadius: '8px', marginBottom: '1.5rem' }}>
                <p style={{ margin: '0 0 0.5rem 0', fontWeight: 'bold', color: 'var(--health-cyan)' }}>{activeItem.name}</p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                  <span>⏱️ {Math.round(activeItem.moving_time / 60)} min</span>
                  <span>📏 {(activeItem.distance / 1000).toFixed(2)} km</span>
                  {(() => {
                    if (!activeItem.average_speed || activeItem.average_speed <= 0) return null;
                    const isCycling = (activeItem.sport_type || activeItem.type || '').toLowerCase().match(/ride|bike|ciclismo/);
                    if (isCycling) {
                      return <span>⚡ {(activeItem.average_speed * 3.6).toFixed(1)} km/h</span>;
                    }
                    const paceSecs = 1000 / activeItem.average_speed;
                    const mins = Math.floor(paceSecs / 60);
                    const secs = Math.floor(paceSecs % 60);
                    if (mins > 50 || isNaN(mins)) return null;
                    return <span>⚡ {mins}:{secs.toString().padStart(2, '0')} /km</span>;
                  })()}
                  {activeItem.average_heartrate && <span>❤️ {Math.round(activeItem.average_heartrate)} ppm</span>}
                </div>
              </div>

              {/* BOTÓN Y SECCIÓN DE ZONAS Y PARCIALES (STRAVA DETAILS) */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1.5rem' }}>
                <button
                  type="button"
                  onClick={handleFetchStravaDetails}
                  disabled={loadingStravaDetails}
                  style={{
                    background: 'rgba(255,255,255,0.02)',
                    border: '1px solid var(--glass-border)',
                    borderRadius: '8px',
                    padding: '0.6rem 1rem',
                    color: 'var(--text-primary)',
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    cursor: loadingStravaDetails ? 'wait' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem',
                    transition: 'all 0.2s',
                    opacity: loadingStravaDetails ? 0.7 : 1
                  }}
                  onMouseOver={(e) => { if (!loadingStravaDetails) e.currentTarget.style.background = 'rgba(255,255,255,0.05)' }}
                  onMouseOut={(e) => { if (!loadingStravaDetails) e.currentTarget.style.background = 'rgba(255,255,255,0.02)' }}
                >
                  {loadingStravaDetails ? (
                    <><RefreshCw size={14} className="animate-spin" /> Cargando datos de Strava...</>
                  ) : showStravaDetails ? (
                    <><BarChart2 size={14} /> Ocultar Zonas y Parciales</>
                  ) : (
                    <><BarChart2 size={14} /> 📊 Ver Zonas y Parciales</>
                  )}
                </button>

                {showStravaDetails && errorStr && (
                  <div style={{ marginTop: '0.5rem', padding: '0.75rem', background: 'rgba(239, 68, 68, 0.1)', border: '1px solid #ef4444', borderRadius: '8px', color: '#fca5a5', fontSize: '0.8rem' }}>
                    Error: {errorStr}
                  </div>
                )}

                {showStravaDetails && stravaDetails && (
                  <ActivityDetails details={stravaDetails} />
                )}
              </div>

              <form onSubmit={handleSave}>
                <label style={{ display: 'block', marginBottom: '0.5rem', color: 'var(--text-secondary)', fontWeight: 'bold' }}>Notas del Entrenador (Feedback):</label>
                <textarea 
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder={appMode === 'coach' ? "Escribe aquí el análisis de la sesión para el atleta..." : "El entrenador aún no ha dejado notas."}
                  readOnly={appMode !== 'coach'}
                  style={{
                    width: '100%', minHeight: '120px', padding: '1rem', borderRadius: '8px',
                    background: 'rgba(0,0,0,0.2)', border: '1px solid var(--glass-border)',
                    color: 'white', resize: 'vertical', fontFamily: 'inherit', marginBottom: '1.5rem'
                  }}
                />
                
                {appMode === 'coach' && (
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
                    <button type="submit" disabled={saving} style={{ background: 'var(--health-cyan)', color: 'black', border: 'none', padding: '0.6rem 1.5rem', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }}>
                      {saving ? 'Guardando...' : 'Guardar Feedback'}
                    </button>
                  </div>
                )}
              </form>
            </>
          ) : (
            // PLANNED OR NEW WORKOUT
            <>
              {appMode === 'coach' ? (
                <form onSubmit={handleSave}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.5rem', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>Deporte</label>
                      <select value={type} onChange={e => setType(e.target.value)} style={{ width: '100%', padding: '0.8rem', background: 'rgba(0,0,0,0.2)', border: '1px solid var(--glass-border)', color: 'white', borderRadius: '8px' }}>
                        {SPORT_TYPES.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                      </select>
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.5rem', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>Duración (min)</label>
                      <input type="number" min="1" value={duration} onChange={e => setDuration(e.target.value)} style={{ width: '100%', padding: '0.8rem', background: 'rgba(0,0,0,0.2)', border: '1px solid var(--glass-border)', color: 'white', borderRadius: '8px' }} />
                    </div>
                  </div>

                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>Título de la sesión</label>
                    <input type="text" required value={title} onChange={e => setTitle(e.target.value)} placeholder="Ej. Series 4x1000m" style={{ width: '100%', padding: '0.8rem', background: 'rgba(0,0,0,0.2)', border: '1px solid var(--glass-border)', color: 'white', borderRadius: '8px' }} />
                  </div>

                  <div style={{ marginBottom: '1.5rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>Descripción / Estructura</label>
                    <textarea 
                      value={description} onChange={e => setDescription(e.target.value)}
                      placeholder="Detalla los bloques de trabajo, zonas de pulso, ritmos..."
                      style={{ width: '100%', minHeight: '100px', padding: '0.8rem', background: 'rgba(0,0,0,0.2)', border: '1px solid var(--glass-border)', color: 'white', borderRadius: '8px', resize: 'vertical' }}
                    />
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    {isPlanned ? (
                      <button type="button" onClick={handleDelete} disabled={saving} style={{ background: 'rgba(239, 68, 68, 0.2)', color: '#ef4444', border: 'none', padding: '0.6rem 1.2rem', borderRadius: '8px', cursor: 'pointer' }}>
                        Eliminar
                      </button>
                    ) : <div></div>}
                    <div style={{ display: 'flex', gap: '1rem' }}>
                      <button type="submit" disabled={saving} style={{ background: 'var(--health-green)', color: '#1a1a1a', border: 'none', padding: '0.6rem 1.5rem', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }}>
                        {saving ? 'Guardando...' : (isPlanned ? 'Actualizar Plan' : 'Crear Plan')}
                      </button>
                    </div>
                  </div>
                </form>
              ) : (
                // Athlete View of Planned Workout
                <div style={{ padding: '0.5rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                    <h3 style={{ margin: 0, color: 'var(--health-cyan)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      {activeItem.isCompleted && '✅'} {activeItem.title}
                    </h3>
                    <span style={{ fontSize: '0.8rem', background: 'rgba(255,255,255,0.1)', padding: '0.3rem 0.6rem', borderRadius: '12px' }}>⏱️ {activeItem.durationMinutes} min</span>
                  </div>
                  <div style={{ fontSize: '0.95rem', color: 'var(--text-secondary)', whiteSpace: 'pre-wrap', lineHeight: '1.5', marginBottom: '2rem', padding: '1rem', background: 'rgba(255,255,255,0.02)', borderRadius: '8px' }}>
                    {activeItem.description || 'Sin descripción detallada.'}
                  </div>

                  {activeItem.isCompleted ? (
                    <div style={{ padding: '1rem', background: 'rgba(16, 185, 129, 0.1)', border: '1px solid rgba(16, 185, 129, 0.3)', borderRadius: '8px' }}>
                      <h4 style={{ color: 'var(--health-green)', marginBottom: '0.5rem', marginTop: 0 }}>¡Entrenamiento Completado!</h4>
                      <p style={{ margin: 0, color: 'white', marginBottom: '0.5rem', fontSize: '0.9rem' }}><strong>RPE (Esfuerzo):</strong> <span style={{ color: `hsl(${120 - (activeItem.rpe * 12)}, 80%, 50%)` }}>{activeItem.rpe}/10</span></p>
                      {activeItem.athleteNotes && (
                        <p style={{ margin: 0, color: 'var(--text-secondary)', fontStyle: 'italic', fontSize: '0.9rem' }}>"{activeItem.athleteNotes}"</p>
                      )}
                    </div>
                  ) : (
                    <form onSubmit={handleComplete} style={{ padding: '1.5rem', background: 'rgba(0,0,0,0.2)', borderRadius: '12px', border: '1px solid var(--glass-border)' }}>
                      <h4 style={{ color: 'white', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: 0 }}>🚀 Check-in post-entreno</h4>
                      
                      <div style={{ marginBottom: '1.5rem' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                          <label style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>Esfuerzo Percibido (RPE)</label>
                          <span style={{ fontWeight: 'bold', color: `hsl(${120 - (athleteRpe * 12)}, 80%, 50%)` }}>{athleteRpe} / 10</span>
                        </div>
                        <input 
                          type="range" min="1" max="10" 
                          value={athleteRpe} onChange={(e) => setAthleteRpe(e.target.value)}
                          style={{ width: '100%', accentColor: `hsl(${120 - (athleteRpe * 12)}, 80%, 50%)` }}
                        />
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '0.3rem' }}>
                          <span>Muy Suave</span>
                          <span>Máximo</span>
                        </div>
                      </div>

                      <div style={{ marginBottom: '1.5rem' }}>
                        <label style={{ display: 'block', marginBottom: '0.5rem', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>¿Cómo te has sentido?</label>
                        <textarea 
                          value={athleteNotes} onChange={e => setAthleteNotes(e.target.value)}
                          placeholder="Ej: Me costó arrancar pero terminé con fuerza..."
                          style={{ width: '100%', minHeight: '60px', padding: '0.8rem', background: 'rgba(255,255,255,0.05)', border: '1px solid var(--glass-border)', color: 'white', borderRadius: '8px', resize: 'vertical' }}
                        />
                      </div>

                      <button type="submit" disabled={saving} style={{ width: '100%', background: 'var(--health-green)', color: '#1a1a1a', border: 'none', padding: '0.8rem', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', transition: 'all 0.2s' }}>
                        {saving ? 'Guardando...' : '✅ Marcar como Completado'}
                      </button>
                    </form>
                  )}
                </div>
              )}
            </>
          )}

        </div>
      </div>
    </div>
  );
};

export default WorkoutModal;
